export interface Product {
  id: string;
  name: string;
  category: string;
  description: string;
  image: string;
  alt: string;
  specifications: {
    purity?: string;
    packaging: string;
    minimumOrder: string;
  };
}

export interface BlogPost {
  id: string;
  title: string;
  excerpt: string;
  image: string;
  alt: string;
  category: string;
  date: string;
  readTime: string;
  href: string;
}

export interface SearchResult {
  type: 'product' | 'blog';
  id: string;
  title: string;
  description: string;
  category: string;
  image: string;
  alt: string;
  href?: string;
}

// All products data
export const allProducts: Product[] = [
// Cooking Oils
{
  id: 'prod_sunflower_oil',
  name: 'Sunflower Oil',
  category: 'Cooking Oils',
  description: 'Premium refined sunflower oil with high oleic content. Perfect for cooking and food manufacturing. Consistent quality in every batch.',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_1b5eaf999-1765022942690.png",
  alt: 'Sunflower oil bottle with golden liquid showing premium quality',
  specifications: {
    purity: '99.9% Pure',
    packaging: '1L, 5L, 20L bottles, Bulk tankers',
    minimumOrder: '500 kg'
  }
},
{
  id: 'prod_canola_oil',
  name: 'Canola Oil',
  category: 'Cooking Oils',
  description: 'Low saturated fat canola oil. Ideal for health-conscious food production and frying. Great for restaurants and food manufacturers.',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_1f6ddb513-1770099088729.png",
  alt: 'Canola oil container showing healthy cooking oil option',
  specifications: {
    purity: 'Food Grade',
    packaging: 'Bulk containers, 20L drums',
    minimumOrder: '1000 kg'
  }
},
{
  id: 'prod_palm_oil',
  name: 'Palm Oil',
  category: 'Cooking Oils',
  description: 'Refined palm oil with excellent stability. Perfect for industrial food processing and bakery applications. Long shelf life.',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_116c870f9-1768054047717.png",
  alt: 'Palm oil showing rich golden color for food processing',
  specifications: {
    purity: 'Refined Food Grade',
    packaging: 'Flexitanks, Drums',
    minimumOrder: '2000 kg'
  }
},
{
  id: 'prod_soybean_oil',
  name: 'Soybean Oil',
  category: 'Cooking Oils',
  description: 'Versatile soybean oil for cooking, salad dressings, and industrial use. Reliable quality for all your needs.',
  image: "https://images.unsplash.com/photo-1629375967748-9bc9bf6c53df",
  alt: 'Soybean oil bottle showing versatile cooking oil',
  specifications: {
    purity: 'Industrial Grade',
    packaging: 'Bulk tankers, IBC containers',
    minimumOrder: '1500 kg'
  }
},
{
  id: 'prod_olive_oil',
  name: 'Olive Oil',
  category: 'Cooking Oils',
  description: 'Extra virgin and refined olive oil. Perfect for premium culinary and retail applications. Authentic Mediterranean quality.',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_196b445bb-1764778043502.png",
  alt: 'Premium olive oil bottle showing high-quality Mediterranean oil',
  specifications: {
    purity: 'Extra Virgin / Refined',
    packaging: '500ml, 1L, 5L bottles',
    minimumOrder: '300 kg'
  }
},
// Chicken Feed
{
  id: 'prod_layer_feed',
  name: 'Layer Feed',
  category: 'Chicken Feed',
  description: 'High-protein formula for optimal egg production. Trusted by poultry farms across Europe. Consistent results every time.',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_172b55ec1-1769325082021.png",
  alt: 'Layer chicken feed showing high-protein poultry nutrition',
  specifications: {
    packaging: '25kg, 50kg bags, Bulk',
    minimumOrder: '1000 kg'
  }
},
{
  id: 'prod_broiler_feed',
  name: 'Broiler Feed',
  category: 'Chicken Feed',
  description: 'Growth-optimized feed for rapid weight gain. Perfect for broiler chickens. Proven formula for best results.',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_114025c7c-1770939793882.png",
  alt: 'Broiler chicken feed showing growth formula nutrition',
  specifications: {
    packaging: '25kg, 50kg bags',
    minimumOrder: '1500 kg'
  }
},
{
  id: 'prod_starter_feed',
  name: 'Starter Feed',
  category: 'Chicken Feed',
  description: 'Specially formulated for chicks in their first weeks. Gives them the best start for healthy growth.',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_1afbe3e09-1770939795069.png",
  alt: 'Starter chicken feed for young chicks showing early nutrition',
  specifications: {
    packaging: '20kg, 40kg bags',
    minimumOrder: '800 kg'
  }
},
{
  id: 'prod_organic_feed',
  name: 'Organic Feed',
  category: 'Chicken Feed',
  description: 'Certified organic chicken feed for eco-friendly farming. Perfect for sustainable poultry operations.',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_1f209eede-1770939794051.png",
  alt: 'Organic chicken feed showing certified eco-friendly nutrition',
  specifications: {
    packaging: '25kg bags, Certified Organic',
    minimumOrder: '500 kg'
  }
},
{
  id: 'prod_grower_feed',
  name: 'Grower Feed',
  category: 'Chicken Feed',
  description: 'Balanced nutrition for chickens in their medium growth stage. Supports healthy development.',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_1fe4e481d-1769325083559.png",
  alt: 'Grower chicken feed for medium-stage poultry nutrition',
  specifications: {
    packaging: '30kg, 50kg bags',
    minimumOrder: '1200 kg'
  }
},
// Fertilizers
{
  id: 'prod_npk_fertilizer',
  name: 'NPK Fertilizer 20-20-20',
  category: 'Fertilizers',
  description: 'Balanced NPK formula for all-purpose crop nutrition. Maximize your yield with this proven formula.',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_17bc3ff46-1765051026926.png",
  alt: 'NPK fertilizer granules showing balanced crop nutrition',
  specifications: {
    purity: '20-20-20 Ratio',
    packaging: '25kg, 50kg bags',
    minimumOrder: '2000 kg'
  }
},
{
  id: 'prod_urea',
  name: 'Urea Fertilizer',
  category: 'Fertilizers',
  description: 'High-nitrogen urea for rapid plant growth. Perfect for green foliage development. Fast-acting results.',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_1c5c1d5ba-1770939794056.png",
  alt: 'Urea fertilizer showing high-nitrogen plant nutrition',
  specifications: {
    purity: '46% Nitrogen',
    packaging: 'Bulk, 50kg bags',
    minimumOrder: '5000 kg'
  }
},
{
  id: 'prod_phosphate',
  name: 'Phosphate Fertilizer',
  category: 'Fertilizers',
  description: 'Phosphorus-rich fertilizer for strong roots and flowering. Essential for healthy plant development.',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_1dfc3cbba-1766332567603.png",
  alt: 'Phosphate fertilizer showing root development nutrients',
  specifications: {
    purity: 'High Phosphorus',
    packaging: '40kg, 50kg bags',
    minimumOrder: '3000 kg'
  }
},
{
  id: 'prod_potash',
  name: 'Potash Fertilizer',
  category: 'Fertilizers',
  description: 'Potassium source for improved crop quality. Boosts disease resistance and stress tolerance.',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_182b0af4e-1770939797635.png",
  alt: 'Potash fertilizer showing potassium for crop quality',
  specifications: {
    purity: 'Muriate of Potash',
    packaging: '50kg bags, Bulk',
    minimumOrder: '2500 kg'
  }
},
{
  id: 'prod_organic_compost',
  name: 'Organic Compost',
  category: 'Fertilizers',
  description: 'Eco-friendly organic compost for sustainable farming. Improves soil health naturally.',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_140322a9d-1770939792106.png",
  alt: 'Organic compost showing eco-friendly soil nutrients',
  specifications: {
    packaging: '30kg bags, Bulk',
    minimumOrder: '1000 kg'
  }
},
// Wood Pellets
{
  id: 'prod_premium_pellets',
  name: 'Premium Wood Pellets 6mm',
  category: 'Wood Pellets',
  description: 'High-quality 6mm wood pellets for residential and commercial heating. Clean burning with low ash content.',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_134b1ef9e-1764865974487.png",
  alt: 'Premium wood pellets showing consistent 6mm size for heating',
  specifications: {
    purity: 'Low Ash Content',
    packaging: '15kg bags, 1 ton pallets',
    minimumOrder: '1000 kg'
  }
},
{
  id: 'prod_industrial_pellets',
  name: 'Industrial Wood Pellets',
  category: 'Wood Pellets',
  description: 'Bulk wood pellets for large-scale industrial heating and power generation. Reliable energy source.',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_1055e3bd4-1770944828078.png",
  alt: 'Industrial wood pellets for large-scale energy production',
  specifications: {
    packaging: 'Bulk delivery, Silo trucks',
    minimumOrder: '10 tons'
  }
},
{
  id: 'prod_hardwood_pellets',
  name: 'Hardwood Pellets',
  category: 'Wood Pellets',
  description: 'Dense hardwood pellets with high heat output. Long burn time for maximum efficiency.',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_1cf2d0955-1770939793313.png",
  alt: 'Hardwood pellets showing high heat output biomass',
  specifications: {
    purity: 'Hardwood',
    packaging: '15kg bags',
    minimumOrder: '500 kg'
  }
},
{
  id: 'prod_softwood_pellets',
  name: 'Softwood Pellets',
  category: 'Wood Pellets',
  description: 'Affordable softwood pellets for everyday heating needs. Good value for money.',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_10dc27772-1770944826832.png",
  alt: 'Softwood pellets showing affordable heating solution',
  specifications: {
    purity: 'Softwood',
    packaging: '15kg bags',
    minimumOrder: '750 kg'
  }
},
{
  id: 'prod_certified_pellets',
  name: 'ENplus Certified Pellets',
  category: 'Wood Pellets',
  description: 'Premium certified wood pellets meeting ENplus A1 standards. Guaranteed quality and performance.',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_1c3515485-1770939795052.png",
  alt: 'ENplus certified wood pellets showing premium quality',
  specifications: {
    purity: 'ENplus A1',
    packaging: '15kg bags, Certified',
    minimumOrder: '1000 kg'
  }
},
// Iron Scraps
{
  id: 'prod_hms_scrap',
  name: 'HMS 1&2 Iron Scrap',
  category: 'Iron Scraps',
  description: 'Heavy melting steel scrap for recycling and steel production. High-quality ferrous metal.',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_1956b083d-1770939797813.png",
  alt: 'HMS iron scrap showing heavy melting steel for recycling',
  specifications: {
    purity: 'HMS 1&2 Grade',
    packaging: 'Bulk containers',
    minimumOrder: '20 tons'
  }
},
{
  id: 'prod_shredded_scrap',
  name: 'Shredded Steel Scrap',
  category: 'Iron Scraps',
  description: 'Processed shredded steel scrap ready for melting. Clean and sorted for easy handling.',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_1956b083d-1770939797813.png",
  alt: 'Shredded steel scrap showing processed metal for recycling',
  specifications: {
    purity: 'Shredded Grade',
    packaging: 'Bulk',
    minimumOrder: '15 tons'
  }
},
{
  id: 'prod_cast_iron',
  name: 'Cast Iron Scrap',
  category: 'Iron Scraps',
  description: 'High-grade cast iron scrap for foundries. Excellent for remelting and casting applications.',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_1a6d7a5d3-1770939792357.png",
  alt: 'Cast iron scrap showing foundry-grade recycled metal',
  specifications: {
    purity: 'Cast Iron',
    packaging: 'Bulk containers',
    minimumOrder: '10 tons'
  }
},
{
  id: 'prod_steel_turnings',
  name: 'Steel Turnings',
  category: 'Iron Scraps',
  description: 'Clean steel turnings from machining operations. Ideal for steel mills and recycling.',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_1956b083d-1770939797813.png",
  alt: 'Steel turnings showing machining scrap for recycling',
  specifications: {
    purity: 'Clean Turnings',
    packaging: 'Bulk',
    minimumOrder: '5 tons'
  }
},
{
  id: 'prod_plate_scrap',
  name: 'Plate & Structural Scrap',
  category: 'Iron Scraps',
  description: 'Heavy plate and structural steel scrap. Perfect for large-scale recycling operations.',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_117ddd05c-1770944767748.png",
  alt: 'Plate and structural steel scrap for industrial recycling',
  specifications: {
    purity: 'Structural Grade',
    packaging: 'Bulk containers',
    minimumOrder: '25 tons'
  }
}];


// All blog posts data
export const allBlogPosts: BlogPost[] = [
{
  id: 'featured_wood_pellets',
  title: "The Future of Sustainable Wood Pellets in 2026",
  excerpt: "Eco-friendly sourcing is changing the biomass energy industry. Learn what it means for businesses investing in renewable heating solutions. We cover the latest trends, certifications, and best practices.",
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_14c4dd2c1-1768255287690.png",
  alt: "Sustainable wood pellets production facility with eco-friendly practices",
  category: "Sustainability",
  date: "February 10, 2026",
  readTime: "8 min read",
  href: "/blog#sustainable-wood-pellets"
},
{
  id: 'post_cooking_oils',
  title: "Top 5 Cooking Oils in High Demand for 2026",
  excerpt: "Which cooking oils are food manufacturers buying right now? We break down the top 5 and explain why they're essential for your business.",
  image: "https://images.unsplash.com/photo-1679604129971-0611abd53ff5",
  alt: "Various cooking oil bottles showing market trends",
  category: "Market Trends",
  date: "February 8, 2026",
  readTime: "5 min read",
  href: "/blog#cooking-oils-2026"
},
{
  id: 'post_chicken_feed',
  title: "How to Choose Quality Chicken Feed for Your Farm",
  excerpt: "Not all chicken feed is created equal. This guide helps you select the right formulation for layers, broilers, and organic operations.",
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_1b2af00d3-1770944826805.png",
  alt: "Chicken feed selection guide showing different feed types",
  category: "Agriculture",
  date: "February 5, 2026",
  readTime: "6 min read",
  href: "/blog#chicken-feed-guide"
},
{
  id: 'post_npk',
  title: "NPK Fertilizers: Understanding the Numbers",
  excerpt: "What do those numbers on fertilizer bags mean? We explain nitrogen, phosphorus, and potassium ratios and how to choose the right formula.",
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_17bc3ff46-1765051026926.png",
  alt: "NPK fertilizer granules showing balanced crop nutrition",
  category: "Education",
  date: "February 1, 2026",
  readTime: "7 min read",
  href: "/blog#npk-fertilizers"
},
{
  id: 'post_iron_recycling',
  title: "Iron Scrap Recycling: Environmental Impact",
  excerpt: "How recycling iron scrap benefits the planet and your bottom line. Learn about the economics of scrap metal recycling.",
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_1149fd5e5-1770939796960.png",
  alt: "Iron scrap recycling showing environmental benefits",
  category: "Sustainability",
  date: "January 28, 2026",
  readTime: "5 min read",
  href: "/blog#iron-recycling"
},
{
  id: 'post_supply_chain',
  title: "Global Supply Chain Trends in 2026",
  excerpt: "Key trends shaping supply and distribution this year. From logistics innovations to geopolitical factors affecting trade.",
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_14abedf49-1767851496109.png",
  alt: "Global supply chain operations showing modern logistics",
  category: "Industry News",
  date: "January 25, 2026",
  readTime: "6 min read",
  href: "/blog#supply-chain-trends"
},
{
  id: 'post_bulk_ordering',
  title: "Bulk Ordering Tips for Businesses",
  excerpt: "Maximize savings and efficiency with smart ordering strategies. Learn about volume discounts and inventory management.",
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_1a0366a63-1770939795061.png",
  alt: "Bulk ordering warehouse showing efficient inventory management",
  category: "Business Tips",
  date: "January 20, 2026",
  readTime: "5 min read",
  href: "/blog#bulk-ordering"
},
{
  id: 'post_certifications',
  title: "Quality Certifications: What They Mean for You",
  excerpt: "Understanding ISO and other certifications in the supply industry. Why they matter and how they protect your business.",
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_15949e003-1764671142241.png",
  alt: "Quality certification documents showing industry standards",
  category: "Quality Standards",
  date: "January 15, 2026",
  readTime: "6 min read",
  href: "/blog#quality-certifications"
},
{
  id: 'post_seasonal_demand',
  title: "Seasonal Demand Patterns in Agriculture",
  excerpt: "Understanding seasonal trends helps you plan better. We analyze demand patterns for feed, fertilizers, and more.",
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_1a79c2185-1770939795268.png",
  alt: "Agricultural seasonal patterns showing demand trends",
  category: "Market Analysis",
  date: "January 10, 2026",
  readTime: "6 min read",
  href: "/blog#seasonal-demand"
},
{
  id: 'post_energy_efficiency',
  title: "Energy Efficiency with Wood Pellets",
  excerpt: "How wood pellets compare to other heating fuels. Cost analysis and environmental benefits explained.",
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_1e9ca589f-1768646377809.png",
  alt: "Wood pellet heating system showing energy efficiency",
  category: "Energy",
  date: "January 5, 2026",
  readTime: "7 min read",
  href: "/blog#energy-efficiency"
}];


// Search function with real-time filtering
export function searchContent(query: string): SearchResult[] {
  if (!query || query.trim().length === 0) {
    return [];
  }

  const searchTerm = query.toLowerCase().trim();
  const results: SearchResult[] = [];

  // Search products
  allProducts.forEach((product) => {
    const nameMatch = product.name.toLowerCase().includes(searchTerm);
    const descMatch = product.description.toLowerCase().includes(searchTerm);
    const categoryMatch = product.category.toLowerCase().includes(searchTerm);

    if (nameMatch || descMatch || categoryMatch) {
      results.push({
        type: 'product',
        id: product.id,
        title: product.name,
        description: product.description,
        category: product.category,
        image: product.image,
        alt: product.alt
      });
    }
  });

  // Search blog posts
  allBlogPosts.forEach((post) => {
    const titleMatch = post.title.toLowerCase().includes(searchTerm);
    const excerptMatch = post.excerpt.toLowerCase().includes(searchTerm);
    const categoryMatch = post.category.toLowerCase().includes(searchTerm);

    if (titleMatch || excerptMatch || categoryMatch) {
      results.push({
        type: 'blog',
        id: post.id,
        title: post.title,
        description: post.excerpt,
        category: post.category,
        image: post.image,
        alt: post.alt,
        href: post.href
      });
    }
  });

  return results;
}

// Generate autocomplete suggestions
export function getAutocompleteSuggestions(query: string): string[] {
  if (!query || query.trim().length < 2) {
    return [];
  }

  const searchTerm = query.toLowerCase().trim();
  const suggestions = new Set<string>();

  // Add product names
  allProducts.forEach((product) => {
    if (product.name.toLowerCase().includes(searchTerm)) {
      suggestions.add(product.name);
    }
    if (product.category.toLowerCase().includes(searchTerm)) {
      suggestions.add(product.category);
    }
  });

  // Add blog titles
  allBlogPosts.forEach((post) => {
    if (post.title.toLowerCase().includes(searchTerm)) {
      suggestions.add(post.title);
    }
    if (post.category.toLowerCase().includes(searchTerm)) {
      suggestions.add(post.category);
    }
  });

  return Array.from(suggestions).slice(0, 5);
}

// Get all unique categories
export function getAllCategories(): {products: string[];blogs: string[];} {
  const productCategories = new Set<string>();
  const blogCategories = new Set<string>();

  allProducts.forEach((p) => productCategories.add(p.category));
  allBlogPosts.forEach((b) => blogCategories.add(b.category));

  return {
    products: Array.from(productCategories),
    blogs: Array.from(blogCategories)
  };
}